import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnDestroy,
	OnInit,
	Output,
	ViewChild,
	Renderer2
} from '@angular/core';
import { Utils } from '../../services/utils.service';
import { SubscriptionLike } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { Cell } from '../table/classes/cell';
import { Header } from '../table/classes/header';
import { SessionService } from '../../services/session.service';
import { SHARED_MODULES } from '../../../shared.';
import { SanitizerPipe } from '../../pipes/sanitizer.pipe';
import { FaIconComponent } from '@fortawesome/angular-fontawesome';

interface TableData {
	data: any[];
	columns: any[];
}

interface Table {
	data: {
		data: Cell[];
		id: number;
		children?: any;
		checked?: boolean;
		indeterminate?: boolean;
		disabled?: boolean;
		is_column?: boolean;
		hidden?: boolean;
	}[];
	columns: Header[];
}

@Component({
    selector: 'app-nested-table',
    templateUrl: './nested-table.component.html',
    styleUrls: ['./nested-table.component.scss'],
	imports: [SHARED_MODULES, SanitizerPipe, FaIconComponent],
    standalone: true
})
export class NestedTableComponent implements OnInit, OnDestroy {
	tableData: TableData;
	table: Table = {
		data: [],
		columns: []
	};
	tableCopy: Table = {
		data: [],
		columns: []
	};
	displayData: Table = {
		data: [],
		columns: []
	};
	groupOption: string = 'stand';

	paginateBtns: any[] = [];
	paginateBy: number = 10;
	page: number = 1;
	maxPage: number = 1;
	allSelected: boolean = false;
	selectingBtns: any[] = [];
	translateSubscription: SubscriptionLike;
	translateSubscription2: SubscriptionLike;
	selectedList: any[] = [];
	clickedList: any[] = [];
	dblClickedList: any[] = [];
	sortByStatus: boolean = false;
	dataGrouped: boolean = false;
	groups_to_expand: any[] = [];
	itemDeleted: boolean = false;
	selectedItems: any = [];
	tableOffsetTop: number;
	standTableColumns: any = [
		'PLANOGRAM',
		'PLANS_QTY',
		'IN_PROGRESS_QTY',
		'EXECUTING_QTY',
		'RETURNED_QTY',
		'EXECUTED_QTY',
		'CHECKED_QTY'
	];
	planogramTableColumns: any = [
		'SHOP_PLAN',
		'STAND_QTY',
		'IN_PROGRESS_QTY',
		'EXECUTING_QTY',
		'RETURNED_QTY',
		'EXECUTED_QTY',
		'CHECKED_QTY'
	];
	standChildrenTableColumns: any = [
		'SHOP_PLAN',
		'PLANOGRAM_STATUS',
		'LAST_UPDATE_DATE',
		'SEND_RETURN_DATE',
		'SEND_RETURN',
		'EXECUTED_CHECKED_DATE',
		'CHECKED_PHOTO_REPORT'
	];
	planogramChildrenTableColumns: any = [
		'PLANOGRAM',
		'PLANOGRAM_STATUS',
		'LAST_UPDATE_DATE',
		'SEND_RETURN_DATE',
		'SEND_RETURN',
		'EXECUTED_CHECKED_DATE',
		'CHECKED_PHOTO_REPORT'
	];
	tableSortingValues: any = {};
	displayHeaderId: number = -1;
	disableAllCheckbox: boolean = false;

	@Input() selecting: boolean = false;
	@Input() showSelectedCount: boolean = true;
	@Input() defaultSortByName: boolean = false;
	@Input() sortInMultiExecution: boolean = false;
	@Input('tableHeight') tableHeight: boolean = false;
	@Input('tableIdx') tableIdx: number;
	@Input('date-format') date_format: string;
	@Output('select') selectEmitter: EventEmitter<any> = new EventEmitter<any>();
	@Output('selected') selectedEmmiter: EventEmitter<any> =
		new EventEmitter<any>();
	@Output('clicked') clickedEmmiter: EventEmitter<any> =
		new EventEmitter<any>();
	@Output('dblclicked') dblClickedEmmiter: EventEmitter<any> =
		new EventEmitter<any>();
	@Output('unselected') unselectedEmmiter: EventEmitter<any> =
		new EventEmitter<any>();
	@Output('selectedAll') selectedAllEmmiter: EventEmitter<any> =
		new EventEmitter<any>();
	@Output('sorted') sorted: EventEmitter<any> = new EventEmitter<any>();
	@Output('paginate') paginate_e: EventEmitter<any> = new EventEmitter<any>();
	@Output('sortedList') sortedList: EventEmitter<any> = new EventEmitter<any>();
	@Output('clicked') clicked: EventEmitter<any> = new EventEmitter<any>();
	@ViewChild('tableElement', { static: true }) tableElement: ElementRef;
	@ViewChild('tableBody') tableBody: ElementRef;
	@ViewChild('thElement') thElement: ElementRef;

	constructor(
		private translate: TranslateService,
		private sessionService: SessionService,
		private renderer: Renderer2
	) {
	}

	@Input('page') set setPage(value) {
		this.page = value || 1;
	}

	@Input('sortTableByStatus') set setSortByStatus(value: boolean) {
		this.sortByStatus = value;
	}

	@Input('selectedItems') set setSelectedItems(value: string) {
		this.selectedItems = value;
	}

	@Input('selectedList') set setSelectedList(value: any[]) {
		this.selectedList = value;
	}

	@Input('table-data') set updateTableData(table: TableData) {
		if (!table.columns.length) return;
		this.tableData = table;
		this.initTable();
	}

	@Input('buttons') set selectingButtons(btns: any[]) {
		this.selectingBtns = btns;
		const keys = btns.map((btn) => btn.title);

		if (!keys.length) return;

		if (this.translateSubscription) {
			this.translateSubscription.unsubscribe();
		}

		this.translateSubscription = this.translate
			.stream(keys)
			.subscribe((translateDict) => {
				this.selectingBtns.forEach((btn) => {
					btn.html = btn.template.replace('<title>', translateDict[btn.title]);
				});
			});
	}

	@Input('dataGrouped') set setDataGrouped(value) {
		this.dataGrouped = value;

		this.selectAllSet();
		this.setDisplayData();
	}

	@Input('table_sorting') set setTableSorting(table_sorting: {
		index: number;
		order: string;
		tableIdx: number;
	}) {
		this.table.columns.forEach((th) => th.clearSorting());
		if (table_sorting && table_sorting.tableIdx == this.tableIdx) {
			this.displayData.columns[table_sorting.index].sorting = true;
			this.displayData.columns[table_sorting.index].order =
				table_sorting.order == 'asc' ? 'desc' : 'asc';
			this.table.data = this.sortInMultiExecution
				? this.table.data.sort(
					this.sortData(this.displayData.columns[table_sorting.index])
				)
				: Utils.arrayDivision(
					this.table.data,
					this.displayData.columns[table_sorting.index].order,
					table_sorting.index
				);
			this.setDisplayData();
		}
	}

	@Input('groupOption') set ChangeGroupOption(value) {
		this.groupOption = value;
	}

	get selectedCount(): number {
		return this.selectedList.filter((row) => row.checked && !row.data[0].parent)
			.length;
	}

	static isGroupedData(data = []) {
		return (data[0] || {}).hasOwnProperty('children');
	}

	static groupDataByParent(data: any[], parents: any[]) {
		const output = {};

		data.forEach((i) => {
			const parent_index = parents.findIndex(
				(p) => p.data[0].displayValue == i.data[0].parent_id[0]
			);

			if (!output[parent_index]) {
				output[parent_index] = [i];
				return;
			}

			output[parent_index].push(i);
		});

		return output;
	}

	sortData(th) {
		const index = this.displayData.columns.indexOf(th),
			order = th.changedOrder,
			thDisplayValue = th.displayValue.toLowerCase();

		return (a, b) => {
			if (
				a.disabled == false ||
				b.disabled == false ||
				a.disabled == true ||
				b.disabled == true
			) {
				const aDisabled = a.disabled,
					bDisabled = b.disabled;
				return Utils.sortPlanogramData({
					firstEl: a.data[index].sortingValue,
					secondEl: b.data[index].sortingValue,
					order: order,
					displayValue: thDisplayValue,
					dateFormat: this.date_format,
					disabledFirstEl: aDisabled,
					disabledSecondEl: bDisabled
				});
			}

			return Utils.generalSorting({
				firstEl: a.data[index].sortingValue,
				secondEl: b.data[index].sortingValue,
				order: order,
				displayValue: thDisplayValue
			});
		};
	}

	sortGroupedData(th) {
		const index = this.displayData.columns.indexOf(th),
			order = th.changedOrder,
			thDisplayValue = th.displayValue.toLowerCase();

		return (a, b) => {
			return Utils.generalSorting({
				firstEl: a.parent[index],
				secondEl: b.parent[index],
				order: order,
				displayValue: thDisplayValue
			});
		};
	}

	sortChildData(th, header) {
		const index = header.indexOf(th),
			order = th.changedOrder,
			thDisplayValue = th.displayValue.toLowerCase();

		return (a, b) => {
			return Utils.generalSorting({
				firstEl: a[index],
				secondEl: b[index],
				order: order,
				displayValue: thDisplayValue
			});
		};
	}

	clickEvent(tr, $event) {
		$event.stopPropagation();
		const obj = {
			tr: tr,
			event: $event
		};
		this.clickedList.push(obj);
		this.clickedEmmiter.emit(obj);
	}

	dblClickEvent(tr, $event) {
		const obj = {
			tr: tr,
			event: $event
		};

		this.dblClickedList.push(obj);
		this.dblClickedEmmiter.emit(obj);
	}

	selectedAction(key) {
		const data = this.table.data.filter((row) => row.checked);
		this.selectEmitter.emit({ data, key });
	}

	ngOnInit() {
		this.tableCopy.data = [...this.table.data];
		this.groups_to_expand = [];

		if (this.defaultSortByName) {
			this.displayData.columns[0].order = 'desc';
			this.sort(this.displayData.columns[0]);
		}
		this.tableOffsetTop = this.tableElement.nativeElement.offsetTop;
	}

	ngOnDestroy() {
		if (this.translateSubscription) {
			this.translateSubscription.unsubscribe();
		}
		if (this.translateSubscription2) {
			this.translateSubscription2.unsubscribe();
		}

		this.translateSubscription = null;
		this.translateSubscription2 = null;
	}

	initTable() {
		this.table.columns = this.tableData.columns.map(
			(column) => new Header(column)
		);

		this.table.data = this.tableData.data.map((row) => {
			const selectedRow = this.selectedList.length
					? this.selectedList.find((item) => item.id == row.parent.id)
					: undefined,
				children = row.children.data.map((child) => {
					const selectedChild = selectedRow
						? selectedRow.children.data.find((item) => item.id == child.id)
						: undefined;

					return {
						checked:
							child.checked || (selectedChild ? selectedChild.checked : false),
						disabled: child.disabled,
						data: child.map((cell) => new Cell(cell)),
						id: child.id,
						parent_id: row.parent.id
					};
				});

			return {
				checked:
					row.parent.checked || (selectedRow ? selectedRow.checked : false),
				disabled: row.parent.disabled,
				data: row.parent.map((cell) => new Cell(cell)),
				children: {
					columns: row.children.columns.map((column) => new Header(column)),
					data: children
				},
				id: row.parent.id
			};
		});

		this.tableSortingValues[-1] = this[`${this.groupOption}TableColumns`].map(
			(column) => new Header(column)
		);

		this.displayData.columns = this.table.columns;

		this.allSelected =
			this.table.data.length &&
			this.table.data.filter((row) => !row.disabled).length > 0 &&
			this.table.data.filter((row) => !row.disabled).length ==
			this.table.data.filter((row) => row.checked).length;

		this.disableAllCheckbox = this.table.data.every((row) => row.disabled);

		this.setDisplayData();
	}

	setDisplayData(paginate: boolean = false) {
		let from = (this.page - 1) * this.paginateBy,
			to = from + this.paginateBy,
			parentIds = this.selectedList.filter((c) => c.checked).map((c) => c.id),
			childrenIds = Utils.to1DArray(
				this.selectedList.map((c) => {
					return c.children.data
						.filter((ch) => ch.checked)
						.map((ch) => {
							return { id: ch.id, parent: ch.parent_id };
						});
				})
			);

		if (NestedTableComponent.isGroupedData(this.tableData.data)) {
			let th = this.displayData.columns.find((s) => s.sorting),
				sortedData = this.tableData.data as any[];

			if (!th) {
				this.displayData.columns[0].sorting = true;
				th = this.displayData.columns[0];
			}

			if (th && this.displayHeaderId == -1) {
				sortedData.sort(this.sortGroupedData(th));
			}

			const data = (
				sortedData.slice(from, to).map((d) => {
					const childrenColumns = [...d.children.columns];
					d.parent.parent = true;
					d.children.data.forEach((c) => {
						c.hidden = !this.groups_to_expand.includes(d.parent['id']);
						c.parent_id = d.parent.id;
					});
					[childrenColumns].forEach((c: any) => {
						c.hidden = !this.groups_to_expand.includes(d.parent['id']);
						c.id = d.parent.id;
						c.disabled = d.parent.disabled;
						c.is_column = true;

						if (!this.tableSortingValues[c.id]) {
							this.tableSortingValues[c.id] = this[
								`${this.groupOption}ChildrenTableColumns`
								].map((column) => new Header(column));
						}
					});
					const th = this.tableSortingValues[d.parent.id].find(
						(s) => s.sorting
					);

					if (th) {
						d.children.data.sort(
							this.sortChildData(th, this.tableSortingValues[d.parent.id])
						);
					}
					return [d.parent, childrenColumns, ...d.children.data];
				}) as any
			)
				.flat()
				.filter((row) => !row.hidden);

			this.displayData.data = data.map((row: any) => {
				return {
					checked:
						parentIds.includes(row['id']) ||
						childrenIds.some(
							(value) =>
								row['id'] == value.id && row['parent_id'] == value.parent
						),
					indeterminate:
						!parentIds.includes(row['id']) &&
						childrenIds.some((value) => row['id'] == value.parent),
					disabled: row['disabled'],
					id: row['id'],
					parent_id: row['parent_id'],
					parent: row['parent'] || false,
					is_column: row['is_column'] || false,
					hidden: row['hidden'] || false,
					data: row.map((cell) => new Cell(cell))
				};
			});
		} else {
			if (from > this.table.data.length) {
				this.page = 1;
				from = 0;
				to = from + this.paginateBy;
			}
			this.displayData.data = this.table.data.slice(from, to);
		}

		// if (this.dataGrouped && ["CATEGORY", "BRAND"].includes(this.displayData.columns[0].value)) {
		//     this.displayData.data.map((tr, idx) => {
		//         if (!tr.data[0].parent) {
		//             return this.setParentSelection(tr, idx);
		//         }
		//     });
		// }

		//this.setSelectedRow();
		this.setPaginateButtons();
		//this.selectAllSet(paginate);
	}

	setPaginateButtons() {
		let btns = [];

		if (NestedTableComponent.isGroupedData(this.tableData.data)) {
			const data = (
				(this.tableData.data as any[]).map((d) => {
					d.parent.parent = true;

					d.children.data.forEach((c) => {
						c.hidden = !this.groups_to_expand.includes(d.parent['id']);
					});

					return [d.parent];
				}) as any
			)
				.flat()
				.filter((row) => !row.hidden);

			this.maxPage = Math.ceil(data.length / this.paginateBy);
		} else {
			this.maxPage = Math.ceil(this.table.data.length / this.paginateBy);
		}
		if (this.maxPage <= 5) {
			for (let i = 1; i <= this.maxPage; i++) {
				btns.push(i);
			}

			this.paginateBtns = btns;
			return;
		}
		if (this.page < 5) {
			for (let i = 1; i <= 5; i++) {
				btns.push(i);
			}

			btns.push('...');
			btns.push(this.maxPage);
			this.paginateBtns = btns;

			return;
		}
		if (this.maxPage - this.page < 4) {
			for (let i = this.maxPage; i >= this.maxPage - 4; i--) {
				btns.push(i);
			}

			btns.push('...');
			btns.push(1);
			btns.reverse();

			this.paginateBtns = btns;
			return;
		}

		this.paginateBtns = [
			1,
			'...',
			this.page - 1,
			this.page,
			this.page + 1,
			'...',
			this.maxPage
		];
	}

	paginateCount(value) {
		this.paginateBy = parseInt(value);
		this.page = 1;
		this.setDisplayData();
	}

	paginate(value) {
		if (typeof value != 'number') return;
		this.paginate_e.emit(value);
		this.page = value;
		this.setDisplayData(true);
	}

	sort(th: Header) {
		if (this.displayHeaderId !== -1) {
			return this.sortChild(
				this.tableSortingValues[this.displayHeaderId],
				this.displayHeaderId,
				true,
				this.displayData.columns.indexOf(th)
			);
		}
		const order = th.changedOrder,
			index = this.displayData.columns.indexOf(th);

		this.table.columns.forEach((th) => th.clearSorting());

		th.sorting = true;
		th.order = order;

		this.table.data = this.sortInMultiExecution
			? this.table.data.sort(this.sortData(th))
			: Utils.arrayDivision(this.table.data, th.order, index);
		this.tableData.data.sort(
			Utils.sortData(
				this.tableData.columns.indexOf(th.value),
				th.order == 'asc' ? 'desc' : 'asc'
			)
		);
		this.setDisplayData();
		this.sorted.emit({
			index: this.displayData.columns.indexOf(th),
			order: th.changedOrder,
			tableIdx: this.tableIdx
		});

		this.sortedList.emit(this.tableData.data);
	}

	sortChild(td, id, isColumn, idx) {
		if (!isColumn) return;

		this.tableSortingValues[id].forEach((th, i) => {
			th.sorting = i == idx;
			th.order = i == idx ? th.changedOrder : 'asc';
		});
		this.setDisplayData();
	}

	onLinkActive(index: number) {
		if (index !== 0) return false;
		const value: string = this.displayData.columns[0].value;

		return (this.sessionService.hasAccess('ph-planogram-view') && value === 'PLANOGRAM') ||
			(this.sessionService.hasAccess('ph-plan', ['edit', 'view']) && value === 'SHOP_PLAN');
	}

	selectAllChange() {
		this.table.data.forEach((row) => {
			if (!row.disabled) {
				row.checked = this.allSelected;
				row.children.data.forEach((child) => {
					if (!child.disabled) child.checked = this.allSelected;
				});
			}
		});

		if (this.allSelected) {
			if (this.table.data.length == this.tableCopy.data.length) {
				this.selectedList = [...this.table.data.filter((row) => row.checked)];
			} else {
				const tempArr = [];
				this.selectedList.forEach((row) => {
					if (this.table.data.find((el) => el['id'] == row.id)) {
						return tempArr.push(row);
					} else return;
				});
				this.selectedList = [
					...this.selectedList.filter((row) => !tempArr.includes(row)),
					...this.table.data.filter((row) => row.checked)
				];
			}
		} else {
			if (this.table.data.length == this.tableCopy.data.length) {
				this.selectedList = [];
			} else {
				this.setStatusInCheckboxes(
					this.selectedList,
					this.table.data,
					this.allSelected,
					true
				);
				this.selectedList = [...this.selectedList.filter((row) => row.checked)];
			}
		}

		this.setDisplayData();
		this.selectedEmmiter.emit(this.selectedList);
	}

	selectHandler(tr, event) {
		let row;

		if (tr.parent || tr.is_column) {
			row = this.table.data.find((r) => r['id'] == tr.id);
			row.children.data
				.filter((child) => !child.disabled)
				.forEach((child) => (child.checked = event));
			row.checked = event;
		} else {
			row = this.table.data.find((r) => r['id'] == tr.parent_id);
			row.children.data.find((child) => child.id == tr.id).checked = event;
			row.checked = row.children.data.every(
				(child) => child.checked || (!child.checked && child.disabled)
			);
		}

		this.selectedList = this.selectedList.filter((item) => item.id != row.id);
		if (!row.children.data.every((child) => !child.checked))
			this.selectedList.push(row);
		this.selectedEmmiter.emit(this.selectedList);

		this.allSelected =
			this.table.data.length &&
			this.table.data.filter((row) => !row.disabled).length > 0 &&
			this.table.data.filter((row) => !row.disabled).length ==
			this.table.data.filter((row) => row.checked).length;

		//if (tr.parent || tr.is_column) this.setChildrenSelection(tr);

		//if (this.dataGrouped && !tr.parent) this.setParentSelection(tr, idx);

		//this.selectAllSet();
		this.setDisplayData();
	}

	selectAllSet(from_paginate: boolean = false) {
		const prev_value = this.allSelected;
		if (
			this.dataGrouped &&
			this.tableData.data.length &&
			['CATEGORY', 'BRAND'].includes(this.displayData.columns[0].value)
		) {
			const currentAmountItemsInTable = (
					this.tableData.data.map((row) => row.children) as any
				).flat().length,
				generalAmountItemsInTableWithoutChecked = this.tableCopy.data
					.filter((row) => {
						if (
							(this.tableData.data.map((el) => el.children) as any)
								.flat()
								.find((el) => el['id'] == row['id'])
						) {
							return row;
						}
					})
					.filter((row) => row.checked).length;

			this.allSelected =
				currentAmountItemsInTable == generalAmountItemsInTableWithoutChecked;
		} else {
			this.allSelected =
				this.table.data.filter((r) => !r.disabled).length ==
				this.table.data
					.filter((r) => !r.disabled)
					.filter((row) => row.checked).length &&
				this.table.data.filter((r) => !r.disabled).length > 0;
		}

		if (prev_value && from_paginate && !this.allSelected) {
			this.allSelected = prev_value;
		}
	}

	toggleChildrenVisibility(tr) {
		if (!tr.parent) return;

		const group_exist = this.groups_to_expand.includes(tr.id);

		if (group_exist) {
			this.groups_to_expand = this.groups_to_expand.filter((id) => id != tr.id);
		} else {
			this.groups_to_expand.push(tr.id);
		}

		this.setDisplayData();
	}

	setChildrenSelection(tr: any) {
		const children = this.tableData.data.find((item) => item.parent.id == tr.id)
			.children.data;

		children.forEach((item) => {
			item.checked = tr.checked;
		});

		const isAllChildrenChecked = children.every((item) => item.checked);

		if (!isAllChildrenChecked) {
			tr.checkedShadow = false;
		}
	}

	setParentSelection(tr: any, idx: number) {
		const parentsIdx = [];
		this.displayData.data.forEach((el, index) => {
			if (el.data[0].parent && index < idx) parentsIdx.push(index);
		});

		if (!parentsIdx.length) return;

		const groupName =
				this.displayData.data[parentsIdx[parentsIdx.length - 1]].data[0].value,
			childrenNames = this.tableData.data
				.find((item) => item.parent[0] == groupName)
				.children.map((item) => item[0]),
			children = this.displayData.data.filter((item) =>
				childrenNames.includes(item.data[0].value)
			),
			isAllChildrenChecked = children.every(
				(item) => item.checked || (!item.checked && item.disabled)
			),
			isAllChildrenDisabled = children.every((item) => item.disabled);

		this.displayData.data[parentsIdx[parentsIdx.length - 1]].checked =
			isAllChildrenChecked && !isAllChildrenDisabled;

		const parent = this.selectedList.find((g) => g.data[0].value == groupName);

		if (parent) {
			parent.checked = isAllChildrenChecked && !isAllChildrenDisabled;
		} else {
			this.selectedList.push(
				this.displayData.data[parentsIdx[parentsIdx.length - 1]]
			);
		}
	}

	transformGroupedData(value: boolean) {
		const data = (
			(this.tableData.data as any[]).map((d) => {
				d.parent.parent = true;
				d.children.data.forEach((c) => {
					c.hidden = !this.groups_to_expand.includes(d.parent['id']);
					c.active = d.active;
					c.is_new = d.is_new;
				});

				return [d.parent, ...d.children];
			}) as any
		).flat();

		return data.map((row) => ({
			checked: value,
			checkedShadow: false,
			id: row['id'],
			data: row.map((cell) => {
				const newCell = new Cell(cell);
				newCell.parent = row['parent'] || false;
				newCell.hidden = row['hidden'] || false;
				newCell.active = row['active'];
				newCell.is_new = row['is_new'];

				return newCell;
			})
		}));
	}

	setStatusInCheckboxes(
		arrayInWhichWeChange: any,
		arrayOnWhichWeCompare: any,
		value: boolean,
		isSelectAll: boolean
	) {
		arrayInWhichWeChange.forEach((row) => {
			if (arrayOnWhichWeCompare.find((el) => el.id == row.id)) {
				return (row.checked = isSelectAll ? value : !value);
			} else {
				return (row.checked = isSelectAll ? !value : value);
			}
		});
	}

	setStatusInTableCopy(tableCopyData: any, selectedData: any, value: boolean) {
		tableCopyData.map((row) => {
			if (selectedData.find((el) => el.id == row.id && el.checked)) {
				return (row.checked = value);
			} else {
				return (row.checked = !value);
			}
		});
	}

	setSelectedRow() {
		if (this.itemDeleted) {
			this.itemDeleted = false;
			this.selectedList = [];

			return;
		}

		if (!this.selectedList.length && this.table.data.some((el) => el.checked)) {
			this.selectedList = this.table.data.filter((el) => el.checked);
			return;
		}

		if (this.selectedItems.length) {
			this.selectedList.forEach(
				(row) =>
					(row.checked = !!this.selectedItems.find(
						(el) =>
							el.stand__main_stand__id == row.id &&
							el.planogram_id == row.planogram_id
					))
			);

			return;
		}
		this.selectedList.map((row) =>
			this.table.data.find((el) => {
				if (el['id'] === row['id'] && !row.disabled) {
					return (row.checked = el.checked);
				}
			})
		);
	}

	setActiveArrow(index: number, order: string) {
		this.displayData.columns[index].order = order;
		this.sort(this.displayData.columns[index]);
	}

	replaceHeader() {
		const openedTableChildren = Array.from(
				this.tableBody.nativeElement.children
			).filter((el: any) => el.className == 'isColumn'),
			columnsTopValues = openedTableChildren.map((el: any) => ({
				id: this.displayData.data[el.sectionRowIndex]['id'],
				height: el.scrollHeight,
				top: el.offsetTop - this.tableOffsetTop
			})),
			tableChildrenRange = columnsTopValues.map((el) => {
				const childrenLength = this.tableData.data.find(
					(d) => d.parent.id == el.id
				).children.data.length;
				return {
					top: el.top,
					bottom: el.top + el.height * childrenLength,
					id: el.id
				};
			}),
			scrollTop = this.tableElement.nativeElement.scrollTop,
			childHeader = tableChildrenRange.find(
				(el) => el.top <= scrollTop && el.bottom > scrollTop
			);

		this.displayHeaderId = childHeader ? childHeader.id : -1;
		this.table.columns = this.tableSortingValues[this.displayHeaderId];
		this.displayData.columns = this.table.columns;
		this.renderer[`${childHeader ? 'add' : 'remove'}Class`](
			this.thElement.nativeElement,
			'childHeader'
		);
	}

	trIsClose(tr: {
		data: Cell[];
		checked?: boolean;
		checkedShadow?: boolean;
		disabled?: boolean;
		is_column?: boolean;
	}) {
		if (tr.is_column) return true;
		const groupExist = this.groups_to_expand.includes(tr['id']);
		return !groupExist;
	}

	rowClick(row, j, event) {
		this.clicked.emit({ row, index: j, event });
	}
}
